import {
  DESCRIBE_WORD_PATH,
  type DescribeWordError,
  type DescribeWordRequest,
  type DescribeWordResponse,
  describeWordErrorSchema,
  describeWordResponseSchema,
} from "../contracts/describe-word";

export type DescribeWordResult =
  | { readonly kind: "answered"; readonly response: DescribeWordResponse }
  | { readonly kind: DescribeWordError["error"] }
  // The Worker could not be reached, or answered something off-contract.
  | { readonly kind: "failed" };

export interface RequestDescribeWordInput {
  readonly request: DescribeWordRequest;
  /** The host's token source, e.g. `fetchToken` from `@bindery/auth-consumer/browser`. */
  readonly getToken: () => Promise<string | null>;
  readonly fetch?: typeof fetch;
}

/**
 * Ask the Worker for the words a description most likely means. Never
 * throws. The description is sent once and kept nowhere: the page builds
 * the `describe` edit and the chosen word's judgement from the response
 * with `describeResultFor` and `describeJudgementFor`.
 */
export const requestDescribeWord = async ({
  request,
  getToken,
  fetch: doFetch = (input, init) => fetch(input, init),
}: RequestDescribeWordInput): Promise<DescribeWordResult> => {
  const token = await getToken();
  if (token === null) return { kind: "unauthenticated" };
  let body: unknown;
  try {
    const response = await doFetch(DESCRIBE_WORD_PATH, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "content-type": "application/json",
      },
      body: JSON.stringify(request),
    });
    body = await response.json();
  } catch {
    return { kind: "failed" };
  }
  const answered = describeWordResponseSchema.safeParse(body);
  if (answered.success) return { kind: "answered", response: answered.data };
  const refused = describeWordErrorSchema.safeParse(body);
  return refused.success ? { kind: refused.data.error } : { kind: "failed" };
};
